/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import { ClearOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ReportService from "../../../../service/ERP/Report.service";
import {
  saveAccessPageStateCookie,
  loadAccessPageStateCookie,
  clearAccessPageStateCookie,
} from "../../../../utils/accessPageStateCookie";
import ModalSalesHistoryDetail from "../../../../components/modal/ERP/sales-history-detail/ModalSalesHistoryDetail";
import {
  customerFgSubColumns,
  customerSummaryColumns,
  fgCustomerSubColumns,
  fgSummaryColumns,
} from "./model";

const reportService = ReportService();
const PAGE_KEY = "erp_sales_history";
const DEFAULT_PAGINATION = { current: 1, pageSize: 20 };

const normPagination = (p) => ({
  current: p?.current ?? 1,
  pageSize: p?.pageSize ?? 20,
  showSizeChanger: true,
});

const groupByFg = (rows) => {
  const map = {};
  rows.forEach((row) => {
    if (!map[row.fg_code]) {
      map[row.fg_code] = {
        fg_code: row.fg_code,
        fg_name: row.fg_name,
        customer_count: 0,
        total_jo: 0,
        total_unit: 0,
        total_carton: 0,
        customers: [],
      };
    }
    const g = map[row.fg_code];
    g.customer_count += 1;
    g.total_jo += Number(row.total_jo || 0);
    g.total_unit += Number(row.total_unit || 0);
    g.total_carton += Number(row.total_carton || 0);
    g.customers.push(row);
  });
  return Object.values(map);
};

const groupByCustomer = (rows) => {
  const map = {};
  rows.forEach((row) => {
    if (!map[row.cuscode]) {
      map[row.cuscode] = {
        cuscode: row.cuscode,
        cusname: row.cusname,
        fg_count: 0,
        total_jo: 0,
        total_unit: 0,
        total_carton: 0,
        fgs: [],
      };
    }
    const g = map[row.cuscode];
    g.fg_count += 1;
    g.total_jo += Number(row.total_jo || 0);
    g.total_unit += Number(row.total_unit || 0);
    g.total_carton += Number(row.total_carton || 0);
    g.fgs.push(row);
  });
  return Object.values(map);
};

export default function SalesHistoryAccess() {
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;

  const [activeSearch, setActiveSearch] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("fg");

  const [fgPagination, setFgPagination] = useState(normPagination(DEFAULT_PAGINATION));
  const [customerPagination, setCustomerPagination] = useState(normPagination(DEFAULT_PAGINATION));

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailSelected, setDetailSelected] = useState({ fg_code: null, cuscode: null, title: "" });

  const fgGroups = useMemo(() => groupByFg(summaryData), [summaryData]);
  const customerGroups = useMemo(() => groupByCustomer(summaryData), [summaryData]);

  const saveToCookie = useCallback(
    (searchValues, fgPag, cusPag) => {
      saveAccessPageStateCookie(PAGE_KEY, {
        searchValues,
        fgPagination: { current: fgPag.current, pageSize: fgPag.pageSize },
        customerPagination: { current: cusPag.current, pageSize: cusPag.pageSize },
      });
    },
    [],
  );

  const handleSearch = useCallback(
    (forcedValues = null) => {
      const run = (values) => {
        const data = { ...values };
        if (data?.jodate) {
          const [jodate_form, jodate_to] = data.jodate.map((d) =>
            dayjs(d).format("YYYY-MM-DD"),
          );
          Object.assign(data, { jodate_form, jodate_to });
          delete data.jodate;
        }

        const resetFg = normPagination({ ...fgPagination, current: 1 });
        const resetCus = normPagination({ ...customerPagination, current: 1 });
        setFgPagination(resetFg);
        setCustomerPagination(resetCus);
        saveToCookie(values, resetFg, resetCus);

        setLoading(true);
        reportService
          .getSalesHistory(data)
          .then((res) => setSummaryData(res?.data?.data || []))
          .catch(() => message.error("โหลดรายงานประวัติการขายไม่สำเร็จ"))
          .finally(() => setLoading(false));
      };

      if (forcedValues !== null) {
        run(forcedValues);
      } else {
        form.validateFields().then(run);
      }
    },
    [form, fgPagination, customerPagination, saveToCookie],
  );

  const handleClear = () => {
    clearAccessPageStateCookie(PAGE_KEY);
    form.resetFields();
    const resetFg = normPagination(DEFAULT_PAGINATION);
    const resetCus = normPagination(DEFAULT_PAGINATION);
    setFgPagination(resetFg);
    setCustomerPagination(resetCus);
    handleSearch({});
  };

  const handleFgTableChange = useCallback(
    (pagination) => {
      const next = normPagination(pagination);
      setFgPagination(next);
      saveToCookie(form.getFieldsValue(true), next, customerPagination);
    },
    [form, customerPagination, saveToCookie],
  );

  const handleCustomerTableChange = useCallback(
    (pagination) => {
      const next = normPagination(pagination);
      setCustomerPagination(next);
      saveToCookie(form.getFieldsValue(true), fgPagination, next);
    },
    [form, fgPagination, saveToCookie],
  );

  useEffect(() => {
    const restored = loadAccessPageStateCookie(PAGE_KEY);
    if (restored?.searchValues) {
      form.setFieldsValue(restored.searchValues);
    }
    if (restored?.fgPagination) {
      setFgPagination(normPagination(restored.fgPagination));
    }
    if (restored?.customerPagination) {
      setCustomerPagination(normPagination(restored.customerPagination));
    }
    handleSearch(restored?.searchValues ?? {});
  }, []);

  const openDetail = useCallback((fg_code, cuscode, title) => {
    setDetailSelected({ fg_code, cuscode, title });
    setDetailVisible(true);
  }, []);

  const fgSubCols = useMemo(
    () =>
      fgCustomerSubColumns({
        onDetail: (row) =>
          openDetail(row.fg_code, row.cuscode, `${row.fg_code} – ${row.cusname}`),
      }),
    [openDetail],
  );

  const customerSubCols = useMemo(
    () =>
      customerFgSubColumns({
        onDetail: (row) =>
          openDetail(row.fg_code, row.cuscode, `${row.cusname} – ${row.fg_code}`),
      }),
    [openDetail],
  );

  const searchPanel = (
    <Collapse
      size="small"
      onChange={setActiveSearch}
      bordered={false}
      activeKey={activeSearch}
      items={[
        {
          key: "1",
          label: (
            <>
              <SearchOutlined />
              <span> ค้นหา</span>
            </>
          ),
          showArrow: false,
          children: (
            <>
              <br />
              <Form form={form} layout="vertical" autoComplete="off">
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={6}>
                    <Form.Item label="FG Code" name="fg_code">
                      <Input placeholder="Enter FG Code" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item label="FG Name" name="fg_name">
                      <Input placeholder="Enter FG Name" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item label="รหัสลูกค้า" name="cuscode">
                      <Input placeholder="Enter Customer Code" allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item label="ชื่อลูกค้า" name="cusname">
                      <Input placeholder="Enter Customer Name" allowClear />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="ช่วงวันที่ JO" name="jodate">
                      <RangePicker
                        placeholder={["From Date", "To Date"]}
                        style={{ width: "100%", height: 40 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={16}>
                    <Flex
                      align="flex-end"
                      justify="flex-end"
                      style={{ height: "100%" }}
                      gap={8}
                    >
                      <Button
                        type="primary"
                        size="small"
                        className="bn-action"
                        icon={<SearchOutlined />}
                        onClick={() => handleSearch()}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        className="bn-action"
                        danger
                        icon={<ClearOutlined />}
                        onClick={handleClear}
                      >
                        ล้าง
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </>
          ),
        },
      ]}
    />
  );

  const tabItems = [
    {
      key: "fg",
      label: "รายงานตาม FG",
      children: (
        <Table
          size="small"
          rowKey="fg_code"
          loading={loading}
          columns={fgSummaryColumns()}
          dataSource={fgGroups}
          scroll={{ x: "max-content" }}
          pagination={fgPagination}
          onChange={handleFgTableChange}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                size="small"
                rowKey="cuscode"
                columns={fgSubCols}
                dataSource={record.customers}
                pagination={false}
                scroll={{ x: "max-content" }}
                style={{ margin: "8px 16px" }}
              />
            ),
          }}
        />
      ),
    },
    {
      key: "customer",
      label: "รายงานตามลูกค้า",
      children: (
        <Table
          size="small"
          rowKey="cuscode"
          loading={loading}
          columns={customerSummaryColumns()}
          dataSource={customerGroups}
          scroll={{ x: "max-content" }}
          pagination={customerPagination}
          onChange={handleCustomerTableChange}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                size="small"
                rowKey="fg_code"
                columns={customerSubCols}
                dataSource={record.fgs}
                pagination={false}
                scroll={{ x: "max-content" }}
                style={{ margin: "8px 16px" }}
              />
            ),
          }}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      {searchPanel}
      <Card>
        <Flex
          justify="space-between"
          align="center"
          className="mb-3"
          wrap
          gap={8}
        >
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            รายงานประวัติการขาย
          </Typography.Title>
          <Typography.Text type="secondary">
            {fgGroups.length} FG · {customerGroups.length} ลูกค้า
          </Typography.Text>
        </Flex>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          destroyInactiveTabPane={false}
        />
      </Card>

      <ModalSalesHistoryDetail
        open={detailVisible}
        close={() => setDetailVisible(false)}
        fg_code={detailSelected.fg_code}
        cuscode={detailSelected.cuscode}
        title={detailSelected.title}
      />
    </Space>
  );
}
